import { findKey, isArray, isString, mapValues, startsWith } from 'lodash-es';

/**
 * Form validation
 *
 * Assign to your components model data
 *   import formValidation from '../../form-validation';
 *   data() {
 *    return {
 *      validation: formValidation(),
 *    }
 *  }
 *
 * created by alexv 5/4/17
 */

export type FormValidationField = {
  valid: boolean;
  message: string;
};

export type FormValidationFields = {
  [key: string]: FormValidationField;
};

export type FormValidation = {
  fields: FormValidationFields;
  ensure(fieldName: string): void;
  notBlank(value: any, fieldName: string, message?: string): void;
  matchesPattern(value: string, fieldName: string, pattern: RegExp, message?: string): void;
  assert(fieldName: string, conditional: (() => boolean) | boolean, message?: string): void;
  formGroupClasses(fieldName: string): { 'has-danger': boolean };
  formControlClasses(fieldName: string): { 'form-control-danger': boolean };
  reset(): void;
  isValid(fieldName: string): boolean;
  isInvalid(fieldName: string): boolean;
  message(fieldName: string): string;
  formValid(): boolean;
  summarize(): { [key: string]: boolean };
};

export default function (): FormValidation {
  return {
    fields: {},
    /**
     * Ensures that the field exists to avoid null references.
     * @param {string} fieldName
     *
     */
    ensure(fieldName: string) {
      if (!this.fields[fieldName]) {
        this.fields[fieldName] = { valid: true, message: '' };
      }
    },
    /**
     * Checks for empty values and flags the field if blank.
     * Example: notBlank(this.form.email, 'email');
     * @param {any} value - the form modal value to validate
     * @param fieldName
     * @param message? (optional) - error message to display
     */
    notBlank(value: any, fieldName: string, message = 'This is a required field') {
      let blank = false;

      this.ensure(fieldName);

      if (!value) {
        blank = true;
      }
      if (isArray(value) && value.length === 0) {
        blank = true;
      }
      if (
        isString(value) &&
        (value.trim().length === 0 || startsWith(value.toLowerCase(), 'choose'))
      ) {
        blank = true;
      }
      if (blank) {
        this.fields[fieldName].valid = false;
        this.fields[fieldName].message = message;
      }
    },
    /**
     * Validate that the field's value matches the given regex pattern
     * On failure, will set the field to invalid and display the error message.
     * @param value - User-entered value
     * @param fieldName
     * @param pattern - regex
     * @param message - Error message to display on failure
     */
    matchesPattern(value: any, fieldName: string, pattern: RegExp, message?: string) {
      this.ensure(fieldName);

      if (!pattern.test(value)) {
        this.fields[fieldName].valid = false;
        this.fields[fieldName].message = message || 'Not valid input';
      }
    },
    /**
     * Assert that the conditional is true
     * @param fieldName
     * @param {Function | Boolean} conditional - a function that returns a boolean, or a boolean
     * @param message
     */
    assert(fieldName: string, conditional: (() => boolean) | boolean, message?: string) {
      this.ensure(fieldName);
      const value = typeof conditional === 'function' ? conditional() : conditional;

      if (!value) {
        this.fields[fieldName].valid = false;
        this.fields[fieldName].message = message || 'Not valid input';
      }
    },
    /**
     * Get an object for setting validation classes for Bootstrap 4 <form-group>.
     * @param {string} fieldName
     * @return {{has-danger: boolean}}
     */
    formGroupClasses(fieldName: string) {
      this.ensure(fieldName);
      return { 'has-danger': this.fields[fieldName].valid === false };
    },
    /**
     * Get an object for setting validation classes for Bootstrap 4 <form-control> element.
     * @param {string} fieldName
     * @return {{form-control-danger: boolean}}
     */
    formControlClasses(fieldName: string) {
      this.ensure(fieldName);
      return { 'form-control-danger': this.fields[fieldName].valid === false };
    },
    /**
     * Clear the validation fields
     */
    reset() {
      this.fields = {};
    },
    /**
     * Is this field currently flagged as valid?
     * @param fieldName
     * @return {boolean}
     */
    isValid(fieldName: string) {
      this.ensure(fieldName);
      return this.fields[fieldName].valid === true;
    },
    /**
     * Is this field currently flagged as invalid?
     * @param fieldName
     * @return {boolean}
     */
    isInvalid(fieldName: string) {
      this.ensure(fieldName);
      return this.fields[fieldName].valid === false;
    },
    /**
     * Get the error message for a given field.
     * @param fieldName
     * @return {string}
     */
    message(fieldName: string) {
      this.ensure(fieldName);
      return this.fields[fieldName].message || '';
    },
    /**
     * Returns true if all fields are valid
     * @return {boolean}
     */
    formValid() {
      return !findKey(this.fields, { valid: false });
    },
    /**
     * Return list of validity status for all fields
     */
    summarize() {
      return mapValues(this.fields, 'valid');
    },
  };
}
